import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner, Input } from 'reactstrap';
import { Result } from '../../models/Results';
import { ShoppingCart } from '../../models/ShoppingCart';

export type ShoppingCartGenericDropdownAsyncProps<ItemID> = {
    loadDataSource: () => Promise<Result<ShoppingCart[]>>;
    dataSourceDependentParams?: any[];
    preprocessDataSource?: (items: ShoppingCart[]) => ShoppingCart[];    
    renderItem: (item: ShoppingCart) => React.ReactNode;
    renderSelectedItem: (item: ShoppingCart) => React.ReactNode;
    onSelectedItemChanged?: (selectedItem: ShoppingCart) => void;
    getId: (item: ShoppingCart) => ItemID;
    selectedValue?: ItemID | undefined;
    allowNullSelection?: boolean;
    nullSelectionText?: string;
    className?: string;
    onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
}

export default function ShoppingCartGenericDropdownAsync<ItemID>(props: ShoppingCartGenericDropdownAsyncProps<ItemID>) {

    const mountRed = useRef(true);
    const [ready, setReady] = useState(false);
    const [dataSource, setDataSource] = useState<ShoppingCart[]>([]);
    const [selectedItem, setSelectedItem] = useState(undefined);
    const [opened, setOpened] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredDataSource, setFilteredDataSource] = useState<ShoppingCart[]>([]);

    const loadDataSource = () => {
        setIsLoading(true);

        props.loadDataSource().then(x => {

            if (!mountRed.current)
                return;

            if (!x.hasErrors) {

                let ds = (props.preprocessDataSource)
                    ? props.preprocessDataSource(x.value)
                    : x.value;

                setDataSource(ds);
                setFilteredDataSource(ds);
            }

            setReady(true);
            setIsLoading(false);
        });
    }

    const recalcSelectedItem = () => {

        let newValue = undefined;

        if (props.selectedValue && dataSource) {
            newValue = dataSource.find(x => props.selectedValue == props.getId(x));
        } 

        if (newValue != selectedItem) {

            setSelectedItem(newValue);

            if (props.onSelectedItemChanged)
                props.onSelectedItemChanged(newValue);
        } 
    }

    useEffect(() => {        

        if (ready) {
            recalcSelectedItem();
        }

    }, [props.selectedValue, dataSource, ready])

    useEffect(() => {

        const ds = dataSource.filter(x => x.CartName.toLowerCase().includes((searchTerm || '').toLowerCase()) || x.CartId === '-1');

        setFilteredDataSource(ds);

    }, [searchTerm])


    // on component mount
    useEffect(() => {

        // keep track of mounted state
        return (() => {            
            mountRed.current = false;
        })

    }, []);


    // reload ds on component mount or datasource dependent params change
    useEffect(() => {

        loadDataSource();

    }, props.dataSourceDependentParams || []);

    return (<React.Fragment>
        <Dropdown className={props.className} onBlur={props.onBlur} isOpen={opened} toggle={() => {
            setOpened(!opened);
            if (!isLoading && !ready) {
                loadDataSource();
            }
        }}>
            <DropdownToggle className="w-100 d-flex border" color="light" onBlur={props.onBlur}>
                <span className="w-100 text-left">{selectedItem && props.renderSelectedItem(selectedItem)}
                    {!selectedItem && (props.nullSelectionText || 'Select')}
                </span>
                <i className="flex-shrink-1 align-self-center fa fa-angle-down"></i>
            </DropdownToggle>
            {
                ready ? <DropdownMenu className="w-100">
                    <div className="dropdown-menu-search">
                        <Input
                            type="text"
                            placeholder="Search Cart..."
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value);
                            }}
                        />
                    </div>
                    <div className="dropdown-menu-inner">
                        {props.allowNullSelection === true &&
                            <DropdownItem key="null-selection" onClick={x => {
                                props.onSelectedItemChanged && props.onSelectedItemChanged(undefined);
                                setSelectedItem(undefined);
                                setSearchTerm("");
                            }} className="clear-selection">
                                {props.nullSelectionText || "Select"}
                            </DropdownItem>
                        }
                        {(filteredDataSource || []).map(
                            (item: ShoppingCart) => <DropdownItem key={props.getId(item).toString()} onClick={x => {
                                if (props.onSelectedItemChanged)
                                    props.onSelectedItemChanged(item);
                                else
                                    setSelectedItem(item);

                                setSearchTerm("");
                            }}>
                                {props.renderItem(item)}
                            </DropdownItem>)}
                    </div>
                </DropdownMenu>
                    : <DropdownMenu>
                        <Spinner />
                    </DropdownMenu>
            }
        </Dropdown>
    </React.Fragment>);
}

